import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BookMarked,
  Plus,
  Edit,
  Trash2,
  Eye,
  Save,
  X,
  Image as ImageIcon,
  AlertCircle,
  Calendar,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';

interface MemoryBook {
  id: string;
  title: string;
  description?: string;
  cover_image?: string;
  is_published: boolean;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

interface MemoryBookChapter {
  id: string;
  memory_book_id: string;
  title: string;
  content?: string;
  chapter_order: number;
  images?: string[];
  created_at: string;
  updated_at: string;
}

export function MemoryBook() {
  const { user } = useAuth();
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [chapters, setChapters] = useState<MemoryBookChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddBookModal, setShowAddBookModal] = useState(false);
  const [showAddChapterModal, setShowAddChapterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState<MemoryBook | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<MemoryBookChapter | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  
  const [bookFormData, setBookFormData] = useState({
    title: '',
    description: '',
    cover_image: ''
  });

  const [chapterFormData, setChapterFormData] = useState({
    title: '',
    content: '',
    chapter_order: 1,
    images: ['']
  });

  useEffect(() => {
    const loadMemoryBooks = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load memory books and their chapters
        const [booksResponse, chaptersResponse] = await Promise.all([
          supabase
            .from('legacy_memory_book')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('legacy_memory_book_chapters')
            .select('*')
            .order('chapter_order', { ascending: true })
        ]);
        
        if (booksResponse.error) throw booksResponse.error;
        if (chaptersResponse.error) throw chaptersResponse.error;
        
        setMemoryBooks(booksResponse.data || []);
        setChapters(chaptersResponse.data || []);
      } catch (err) {
        console.error('Error loading memory books:', err);
        setError(err instanceof Error ? err.message : 'Failed to load memory books');
      } finally {
        setLoading(false);
      }
    };
    
    loadMemoryBooks();
  }, [user]);

  const handleAddBook = () => {
    setBookFormData({
      title: '',
      description: '',
      cover_image: ''
    });
    setIsEditing(false);
    setShowAddBookModal(true);
  };

  const handleEditBook = (book: MemoryBook) => {
    setSelectedBook(book);
    setBookFormData({
      title: book.title,
      description: book.description || '',
      cover_image: book.cover_image || ''
    });
    setIsEditing(true);
    setShowAddBookModal(true);
  };

  const handleDeleteBook = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this memory book? This will also delete all chapters. This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('legacy_memory_book')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setMemoryBooks(memoryBooks.filter(book => book.id !== id));
        setChapters(chapters.filter(chapter => chapter.memory_book_id !== id));
      } catch (err) {
        console.error('Error deleting memory book:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete memory book');
      }
    }
  };

  const handleSaveBook = async () => {
    if (!bookFormData.title) {
      setError('Please enter a title for the memory book');
      return;
    }
    
    try {
      if (isEditing && selectedBook) {
        // Update existing book
        const { error } = await supabase
          .from('legacy_memory_book')
          .update({
            title: bookFormData.title,
            description: bookFormData.description || null,
            cover_image: bookFormData.cover_image || null
          })
          .eq('id', selectedBook.id);
          
        if (error) throw error;
        
        setMemoryBooks(memoryBooks.map(book => 
          book.id === selectedBook.id 
            ? {
                ...book,
                title: bookFormData.title,
                description: bookFormData.description || undefined,
                cover_image: bookFormData.cover_image || undefined
              } 
            : book
        ));
      } else {
        // Add new book
        const { data, error } = await supabase
          .from('legacy_memory_book')
          .insert([{
            user_id: user?.id,
            title: bookFormData.title,
            description: bookFormData.description || null,
            cover_image: bookFormData.cover_image || null
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        setMemoryBooks([data, ...memoryBooks]);
      }
      
      setShowAddBookModal(false);
      setSelectedBook(null);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error saving memory book:', err);
      setError(err instanceof Error ? err.message : 'Failed to save memory book');
    }
  };

  const handleAddChapter = (bookId: string) => {
    setSelectedBook(memoryBooks.find(book => book.id === bookId) || null);
    setChapterFormData({
      title: '',
      content: '',
      chapter_order: chapters.filter(c => c.memory_book_id === bookId).length + 1,
      images: ['']
    });
    setIsEditing(false);
    setShowAddChapterModal(true);
  };

  const handleEditChapter = (chapter: MemoryBookChapter) => {
    setSelectedChapter(chapter);
    setChapterFormData({
      title: chapter.title,
      content: chapter.content || '',
      chapter_order: chapter.chapter_order,
      images: chapter.images || ['']
    });
    setIsEditing(true);
    setShowAddChapterModal(true);
  };

  const handleDeleteChapter = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this chapter? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('legacy_memory_book_chapters')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setChapters(chapters.filter(chapter => chapter.id !== id));
      } catch (err) {
        console.error('Error deleting chapter:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete chapter');
      }
    }
  };

  const handleSaveChapter = async () => {
    if (!chapterFormData.title || !selectedBook) {
      setError('Please enter a title for the chapter');
      return;
    }
    
    // Filter out empty images
    const filteredImages = chapterFormData.images.filter(img => img.trim() !== '');
    
    try {
      if (isEditing && selectedChapter) {
        // Update existing chapter
        const { error } = await supabase
          .from('legacy_memory_book_chapters')
          .update({
            title: chapterFormData.title,
            content: chapterFormData.content || null,
            chapter_order: chapterFormData.chapter_order,
            images: filteredImages.length > 0 ? filteredImages : null
          })
          .eq('id', selectedChapter.id);
          
        if (error) throw error;
        
        setChapters(chapters.map(chapter => 
          chapter.id === selectedChapter.id 
            ? {
                ...chapter,
                title: chapterFormData.title,
                content: chapterFormData.content || undefined,
                chapter_order: chapterFormData.chapter_order,
                images: filteredImages.length > 0 ? filteredImages : undefined
              } 
            : chapter
        ));
      } else {
        // Add new chapter
        const { data, error } = await supabase
          .from('legacy_memory_book_chapters')
          .insert([{
            memory_book_id: selectedBook.id,
            title: chapterFormData.title,
            content: chapterFormData.content || null,
            chapter_order: chapterFormData.chapter_order,
            images: filteredImages.length > 0 ? filteredImages : null
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        setChapters([...chapters, data]);
      }
      
      setShowAddChapterModal(false);
      setSelectedChapter(null);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error saving chapter:', err);
      setError(err instanceof Error ? err.message : 'Failed to save chapter');
    }
  };

  const toggleBookExpansion = (bookId: string) => {
    const newExpanded = new Set(expandedBooks);
    if (newExpanded.has(bookId)) {
      newExpanded.delete(bookId);
    } else {
      newExpanded.add(bookId);
    }
    setExpandedBooks(newExpanded);
  };

  const getBookChapters = (bookId: string) => {
    return chapters
      .filter(chapter => chapter.memory_book_id === bookId)
      .sort((a, b) => a.chapter_order - b.chapter_order);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Memory Books</h1>
          <p className="text-gray-600 mt-1">Create and organize your life stories and memories</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddBook}
          icon={<Plus size={20} />}
        >
          Create Memory Book
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {memoryBooks.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookMarked className="text-amber-600" size={32} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Memory Books Yet</h3>
            <p className="text-gray-600 mb-6">
              Start creating your first memory book to preserve and share your life stories.
            </p>
            <Button
              variant="primary"
              onClick={handleAddBook}
              icon={<Plus size={20} />}
            >
              Create Your First Memory Book
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {memoryBooks.map(book => {
            const bookChapters = getBookChapters(book.id);
            const isExpanded = expandedBooks.has(book.id);
            
            return (
              <Card key={book.id}>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-amber-100 rounded-lg">
                        <BookMarked className="text-amber-600" size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{book.title}</h3>
                        <p className="text-sm text-gray-500">
                          {bookChapters.length} chapters • Created {formatDate(book.created_at)}
                        </p>
                        {book.description && (
                          <p className="text-sm text-gray-600 mt-1">{book.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleBookExpansion(book.id)}
                        icon={isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      >
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAddChapter(book.id)}
                        icon={<Plus size={16} />}
                      >
                        Add Chapter
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditBook(book)}
                        icon={<Edit size={16} />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBook(book.id)}
                        icon={<Trash2 size={16} />}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="mt-6 space-y-3">
                      {bookChapters.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No chapters yet. Add your first chapter to start building your memory book.</p>
                        </div>
                      ) : (
                        bookChapters.map(chapter => (
                          <div key={chapter.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  Chapter {chapter.chapter_order}: {chapter.title}
                                </h4>
                                {chapter.content && (
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                    {chapter.content}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditChapter(chapter)}
                                  icon={<Edit size={16} />}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteChapter(chapter.id)}
                                  icon={<Trash2 size={16} />}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Memory Book Modal */}
      <Modal
        isOpen={showAddBookModal}
        onClose={() => setShowAddBookModal(false)}
        title={isEditing ? 'Edit Memory Book' : 'Create Memory Book'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={bookFormData.title}
              onChange={(e) => setBookFormData({ ...bookFormData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Enter memory book title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={bookFormData.description}
              onChange={(e) => setBookFormData({ ...bookFormData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={3}
              placeholder="Describe your memory book"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cover Image URL
            </label>
            <input
              type="url"
              value={bookFormData.cover_image}
              onChange={(e) => setBookFormData({ ...bookFormData, cover_image: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowAddBookModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveBook}
              icon={<Save size={16} />}
            >
              {isEditing ? 'Update' : 'Create'} Memory Book
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Chapter Modal */}
      <Modal
        isOpen={showAddChapterModal}
        onClose={() => setShowAddChapterModal(false)}
        title={isEditing ? 'Edit Chapter' : 'Add Chapter'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chapter Title *
            </label>
            <input
              type="text"
              value={chapterFormData.title}
              onChange={(e) => setChapterFormData({ ...chapterFormData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Enter chapter title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chapter Order
            </label>
            <input
              type="number"
              value={chapterFormData.chapter_order}
              onChange={(e) => setChapterFormData({ ...chapterFormData, chapter_order: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              min="1"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <textarea
              value={chapterFormData.content}
              onChange={(e) => setChapterFormData({ ...chapterFormData, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={6}
              placeholder="Write your chapter content..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowAddChapterModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveChapter}
              icon={<Save size={16} />}
            >
              {isEditing ? 'Update' : 'Add'} Chapter
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}